import React, { FC, useEffect, useState, memo, FormEvent, useRef, ReactElement } from 'react';
import i18next from 'i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { Button, Col } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import ChatMessage from './ChatMessage';
import { useChat } from '../../contexts/chat-context';
import BootstrapAvatar from '../BootstrapAvatar';
import EmptyState from '../EmptyState';
import { ChatHeadDto } from '../../../../shared/dtos/chat.dto';
import { IChatMessage } from '../../util/types';

type Props = { therapy: ChatHeadDto, therapist: any, isMobile: boolean };

const ChatMessageComponent: FC<Props> = ({ therapy, therapist, isMobile }) => {
  const navigate: NavigateFunction = useNavigate();
  const { sendMessage, resendMessage, loadMessages, closeMessages, loadMoreMessages, messages, hasMore } = useChat();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstLoadRef = useRef(true);

  const [formValue, setFormValue] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  const getContainer = (): HTMLElement | null => containerRef.current ?? document.getElementById('chatMessage');

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth'): void => {
    const element: HTMLElement | null = getContainer();
    if (!element) {
      return;
    }

    element.scrollTo({ top: element.scrollHeight, behavior });
  };

  const loadMore = (): void => {
    void loadMoreMessages();
  };

  useEffect(() => {
    firstLoadRef.current = true;
    (async () => {
      await loadMessages(therapy);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom('auto');
          firstLoadRef.current = false;
        });
      });
    })();

    return (): void => closeMessages();
  }, [therapy.id]);

  const handleResendMessage = async (text: any): Promise<void> => {
    await resendMessage({ text: text, therapy });
    scrollToBottom('smooth');
  };
  
  const handleSend = async (e: React.KeyboardEvent | FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formValue.trim()) {return;}

    await sendMessage({ text: formValue, therapy });
    setFormValue('');
    scrollToBottom('smooth');
  };

  const patientName: string = `${therapy.patientFirstName} ${therapy.patientLastName}`;

  if (!therapy) {
    return null;
  }

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, minWidth: 0 }}>
      <div className="d-none d-lg-flex align-items-center gap-3 py-3 px-4 border-bottom">
        <BootstrapAvatar
          name={patientName}
          size={40}
          color="#707089"
          className="rounded-circle"
          round={true}
        />
        <span className="fw-semibold flex-grow-1 text-truncate">{patientName}</span>
        <Button variant="outline-primary" size="sm" onClick={() => navigate(`/therapien/${therapy.therapyId}`)}>
          {i18next.t('chat.openPatientProfile')}
        </Button>
      </div>

      {messages.length > 0 ? (
        <div
          id="chatMessage"
          ref={containerRef}
          className="d-flex flex-column-reverse px-4 pt-4"
          style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}
        >
          <InfiniteScroll
            className="p-4"
            dataLength={messages.length}
            next={loadMore}
            hasMore={hasMore}
            inverse={true}
            scrollableTarget="chatMessage"
            scrollThreshold="150px"
            loader={
              <Col md={12} className="loader text-center" key={0}>
                <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true" />
              </Col>
            }
            style={{ display: 'flex', flexDirection: 'column-reverse', minHeight: '100%' }}
          >
            {messages.map((message: IChatMessage): ReactElement => (
              <ChatMessage
                key={`msg-${message.createdAt}-${message.senderId}`}
                message={message}
                therapist={therapist}
                resendMessage={handleResendMessage}
              />
            ))}
            {isMobile ? (<><br /><br /></>) : (<br />)}
          </InfiniteScroll>
        </div>
      ) : (
        <div className="p-4" style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <EmptyState icon="fas fa-comments" message={i18next.t('chat.noChatMessageFound')} />
        </div>
      )}

      <div className="flex-shrink-0 bg-white" style={{ zIndex: 1030 }}>
        <form onSubmit={handleSend} id="sendMsgForm">
          <div className="d-flex align-items-center gap-2 py-3 px-4 border-top">
            <input
              type="text"
              className="form-control rounded-pill border-0 px-4"
              style={{ backgroundColor: '#f0f0f3', minHeight: 40 }}
              placeholder={i18next.t('chat.typeMsgPlaceholder')}
              value={formValue}
              onChange={e => setFormValue(e.target.value)}
              onKeyUp={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && !e.shiftKey && formValue) {
                  void handleSend(e);
                }
              }}
            />
            <button
              type="submit"
              className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 40, height: 40 }}
              disabled={!formValue}>
              <i className="fas fa-paper-plane" aria-hidden="true" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default memo(ChatMessageComponent);
